@if (showHeader()) {
  <div class="flex items-center gap-2 px-3.5 py-3 border-b border-default shrink-0">
    <h2 class="ds-heading-6-semibold">Belegvermerke</h2>
  </div>
}

@if (isLoading()) {
  <div class="flex flex-1 items-center justify-center py-8">
    <soft-loading-spinner />
  </div>
} @else if (loadFailed()) {
  <div
    class="flex flex-1 flex-col items-center justify-center gap-2 px-3.5 py-8 text-center note-muted"
    data-testid="document-notes-error"
  >
    <soft-ds-icon name="fa-regular fa-circle-exclamation" class="text-2xl" />
    <span class="ds-body-2">Belegvermerke konnten nicht geladen werden.</span>
    <span class="ds-caption">
      Für diese Belegart werden keine Belegvermerke unterstützt.
    </span>
    <soft-ds-button
      variant="outline"
      size="sm"
      data-testid="document-notes-retry"
      (click)="reload()"
    >
      Erneut laden
    </soft-ds-button>
  </div>
} @else {
  <div
    #messages
    class="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 px-3.5 py-3"
    data-testid="document-notes-messages"
  >
    @if (createdActivity(); as activity) {
      <div class="note-activity ds-caption" data-testid="document-notes-activity">
        <span class="note-activity__dot">•</span>
        <span class="note-activity__text">
          {{ activity.user }} hat {{ activity.type }} erstellt
        </span>
        @if (activity.date) {
          <!-- Belegdatum is date-only; no time to show. -->
          <span class="note-activity__date">
            {{ activity.date | date: 'd. MMMM yyyy' }}
          </span>
        }
      </div>
    }

    @if (notes().length === 0) {
      <div class="flex flex-col items-center justify-center gap-2 my-auto note-muted">
        <soft-ds-icon name="fa-regular fa-message-lines" class="text-2xl" />
        <span class="ds-body-2">Keine Belegvermerke vorhanden.</span>
      </div>
    }

    @for (note of sortedNotes(); track note.id) {
      @if (editingId() === note.id) {
        <div
          class="flex flex-col gap-2 rounded-md border border-default bg-surface p-3"
          [attr.data-testid]="'document-note-' + note.id"
        >
          <soft-masterdata-input
            [entity]="'document-note-category'"
            mode="select"
            label="Art"
            [value]="editCategory()"
            (valueChange)="editCategory.set($event)"
            data-testid="document-note-edit-category"
          />
          <soft-ds-multiline-input
            [value]="editText()"
            (valueChange)="editText.set($event ?? '')"
            (keydown.enter)="onEditComposerEnter($event)"
            [lines]="4"
            placeholder="Vermerk eingeben…"
          />
          <div class="flex items-center justify-end gap-2">
            <soft-ds-button
              variant="ghost"
              size="sm"
              [attr.data-testid]="'document-note-edit-cancel-' + note.id"
              (click)="cancelEdit()"
            >
              Abbrechen
            </soft-ds-button>
            <soft-ds-button
              variant="primary"
              size="sm"
              [attr.data-testid]="'document-note-edit-save-' + note.id"
              [disabled]="isSaving() || !editText().trim() || !editCategoryKey()"
              (click)="saveEdit()"
            >
              Speichern
            </soft-ds-button>
          </div>
        </div>
      } @else {
        <div class="flex flex-col gap-1 min-w-0">
          @if (!isOwn(note)) {
            <span class="ds-caption-semibold note-author">{{ userLabel(note) }}</span>
          }
          <soft-ds-chat-bubble
            [variant]="isOwn(note) ? 'user' : 'assistant'"
            [attr.data-testid]="'document-note-' + note.id"
          >
            <p class="ds-body-2 whitespace-pre-wrap wrap-break-word">{{ note.text }}</p>
            @if (canEdit()) {
              <ng-template softSlot="actions">
                <span class="note-actions">
                  <button
                    type="button"
                    class="note-action-btn"
                    title="Bearbeiten"
                    [attr.data-testid]="'document-note-edit-' + note.id"
                    [disabled]="isSaving()"
                    (click)="startEdit(note)"
                  >
                    <soft-ds-icon name="fa-regular fa-pen" />
                  </button>
                  <button
                    type="button"
                    class="note-action-btn"
                    title="Löschen"
                    [attr.data-testid]="'document-note-delete-' + note.id"
                    [disabled]="isSaving()"
                    (click)="deleteNote(note)"
                  >
                    <soft-ds-icon name="fa-regular fa-trash" />
                  </button>
                </span>
              </ng-template>
            }
          </soft-ds-chat-bubble>
          <span class="note-meta ds-caption" [class.text-right]="isOwn(note)">
            {{ note.lastChanged | date: 'dd.MM.yyyy HH:mm' }}
            · {{ note.category?.name || note.category?.key }}
          </span>
        </div>
      }
    }
  </div>

  @if (canEdit()) {
    <div class="shrink-0 flex flex-col gap-2 border-t border-default px-3.5 py-3">
      <soft-masterdata-input
        [entity]="'document-note-category'"
        mode="select"
        autoSelectFirst
        label="Art"
        [showLabel]="false"
        [value]="newCategory()"
        (valueChange)="newCategory.set($event)"
        data-testid="document-note-category"
      />
      <div class="flex items-end gap-2">
        <soft-ds-multiline-input
          class="flex-1 min-w-0"
          [value]="newText()"
          (valueChange)="newText.set($event ?? '')"
          (keydown.enter)="onAddComposerEnter($event)"
          [lines]="2"
          placeholder="Vermerk eingeben…"
        />
        <soft-ds-button
          variant="primary"
          mode="iconOnly"
          title="Senden"
          size="lg"
          data-testid="document-note-send"
          [disabled]="isSaving() || !newText().trim() || !newCategoryKey()"
          (click)="saveAdd()"
        >
          <soft-ds-icon name="fa-regular fa-paper-plane" />
        </soft-ds-button>
      </div>
    </div>
  }
}
